import { Accept } from 'react-dropzone';
import { UseConfirmDialogProps } from '../hooks/useConfirmDialog';
import { getCurrentEnvironment } from '../helpers/determine-env';

export const ApiConfig = {
  public_url: process.env.REACT_APP_BASE_URL,
  api_base_url: process.env.REACT_APP_API_BASE_URL,
  access_token_key: 'access_token',
};

const domainMap: Record<string, string> = {
  dev: process.env.REACT_APP_AUTH_CONFIG_DOMAIN_STAGE || '',
  preprod: process.env.REACT_APP_AUTH_CONFIG_DOMAIN_STAGE || '',
  prod: process.env.REACT_APP_AUTH_CONFIG_DOMAIN_PROD || '',
  local: process.env.REACT_APP_AUTH_CONFIG_DOMAIN_STAGE || '',
};

const clientIdMap: Record<string, string> = {
  dev: process.env.REACT_APP_AUTH_CLIENT_ID_STAGE || '',
  preprod: process.env.REACT_APP_AUTH_CLIENT_ID_STAGE || '',
  prod: process.env.REACT_APP_AUTH_CLIENT_ID_PROD || '',
  local: process.env.REACT_APP_AUTH_CLIENT_ID_STAGE || '',
};

const redirectUriMap: Record<string, string> = {
  dev: process.env.REACT_APP_AUTH_REDIRECT_URI_DEV || '',
  preprod: process.env.REACT_APP_AUTH_REDIRECT_URI_STAGE || '',
  prod: process.env.REACT_APP_AUTH_REDIRECT_URI_PROD || '',
  local: process.env.REACT_APP_AUTH_REDIRECT_URI_LOCAL || '', // fallback for localhost
};

export const AppConfigSSO = {
  domain: domainMap[getCurrentEnvironment()],
  clientId: clientIdMap[getCurrentEnvironment()],
  redirectUri: redirectUriMap[getCurrentEnvironment()],

  charset: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
};

export const AppConfig = {
  pageSizeLabel: 'Display:',
  defaultPageSize: 100,
  pageSizeOptions: [20, 50, 100, 300, 500],
  pageSizeOptionsLargeOnly: [100, 300, 500],
  defaultDisplayDateFormat: 'DD MMM YY HH:mm:ss',
  version: process.env.REACT_APP_RELEASE_VERSION,
  baseImageUrl: process.env.REACT_APP_BASE_IMAGE_URL,
};

export type CoordinatesType = 'Default' | 'NearReader' | 'Facility' | 'AirportGeofence' | 'Calibration' | 'Centroid' | 'CentroidCircle' | 'Sighting';

// Get default Coordinates properties per type
export const getCoordinatesPreset = (type: CoordinatesType): { _type: CoordinatesType; color: string; fillColor: string; fillOpacity?: number } => {
  return {
    _type: type,
    ...getColorByType(type),
  };
};

const getColorByType = (type: CoordinatesType): { color: string; fillColor: string; fillOpacity?: number } => {
  const colorMap: { [key in CoordinatesType]: { color: string; fillColor: string; fillOpacity?: number } } = {
    Default: { color: 'purple', fillColor: 'purple' },
    NearReader: { color: '#98989887', fillColor: 'gray', fillOpacity: 0.1 },
    Facility: { color: 'purple', fillColor: 'purple' },
    AirportGeofence: { color: 'olive', fillColor: 'olive' },
    Calibration: { color: 'yellow', fillColor: 'yellow', fillOpacity: 0.1 },
    Centroid: { color: 'red', fillColor: 'red' },
    CentroidCircle: { color: 'green', fillColor: 'green', fillOpacity: 0.1 },
    Sighting: { color: 'black', fillColor: 'black' },
  };

  return colorMap[type] || { color: '#000000', fillColor: '#000000' }; // Default color and fillColor if the type doesn't match
};

export const MapConfig = {
  DEFAULT_ZOOM: 16,
  DEFAULT_CIRCLE_RADIUS: 280,
  DEFAULT_HEIGHT: '677px',
  DEFAULT_WIDTH: '100%',
  DEFAULT_COLOR: 'purple',

  EDITCONTROL_POLYGON: true,
  EDITCONTROL_RECTANGLE: false,
  EDITCONTROL_POLYLINE: false,
  EDITCONTROL_CLICLE: false,
  EDITCONTROL_MARKER: false,
  EDITCONTROL_CIRCLEMARKER: false,

  ToggleTabSettings: {
    AllTabs: ['nearReader', 'facilities', 'airportGeofence', 'calibration'],
    Default: ['facilities'],
  },
};

export const MapActionView = {
  NearReader: 'nearReader',
  Facilities: 'facilities',
  AirportGeofence: 'airportGeofence',
  Calibration: 'calibration',
};

export const ErrorMessages = {
  Error: 'Error',
  ErrorUnauthorized: 'You are not allowed to access this resource',
  ErrorBadRequest: 'Bad request',
  ErrorResourceNotFound: '404 Resource Not Found',
  ErrorInternalServer: 'Internal Server Error',
  ErrorNotAllowPermission: 'You are not allowed to access this resource. Please contact administrator.',
};

export const Copyright = {
  url: 'https://www.unilode.com/',
  name: 'E-ULD Digital Portal By Unilode',
};

export const CONFIRM_DIALOG_CANCEL: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this record?',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_LANGUAGE_LABEL_DELETE: UseConfirmDialogProps = {
  title: `This action may impact the e-ULD app. Are you certain this label isn't in use elsewhere?`,
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_DIALOG_ACCOUNT_DELETE: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this user?',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_DIALOG_UN_ASSIGN_ROLE: UseConfirmDialogProps = {
  title: 'Are you sure you want to remove the role from this user?',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Remove',
};

export const CONFIRM_DIALOG_REVOKE_PERMISSION: UseConfirmDialogProps = {
  title: 'Are you sure you want to revoke the permission from this user?',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Remove',
};

export const CONFIRM_DIALOG_ROLE_DELETE: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this role? This may affect permissions for users with this role!',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_DIALOG_POLICY_DELETE: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this policy? This may affect permissions for users with this policy!',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_DIALOG_IMAGE_DELETE: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this image?',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_DIALOG_EXPORT_DELETE: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this export?',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const CONFIRM_DIALOG_FILE_DELETE: UseConfirmDialogProps = {
  title: 'Are you sure you want to delete this file?',
};

export const CONFIRM_DIALOG_CONTRACT_DELETE: UseConfirmDialogProps = {
  title: 'This action might impact multiple resources and multiple users. Please confirm you are sure this contract is safe to be deleted.',
  cancelButtonTitle: 'Cancel',
  okButtonTitle: 'Delete',
};

export const SettingName = {
  ReaderOpsStatus: 'ReaderOpsStatus',
  ReaderSupplyChainStatus: 'ReaderSupplyChainStatus',
  ReaderUsageType: 'RaderUsageType',
  EquipmentType: 'EquipmentType',
  EquipmentCategory: 'EquipmentCategory',
  LocationMethod: 'LocationMethod',
  EquipmentStatus: 'EquipmentStatus',
  GeofenceType: 'GeofenceType',
  GeofenceLevel: 'GeofenceLevel',
  FacilityType: 'FacilityType',
  CustomerTypes: 'CustomerTypes',
  SupplyChainStatusColor: 'ReaderSupplyChainStatusColor',
  OpsStatusColor: 'ReaderOpsStatusColor',
  UldStatus: 'UldStatus',
  TaskStatusColor: 'TaskStatusColor',
  TaskStatus: 'TaskStatus',
  TaskSeverity: 'TaskSeverity',
  PairingValidationStatus: 'PairingValidationStatus',
};

export const SettingScope = {
  static: 'STATIC',
  shared: 'SHARED',
};

export const PairingMethods = ['Unknown', 'OCR', 'Barcode', 'QRCode', 'Manual', 'Backoffice'];
export const PairingValidationStatus = ['Pending', 'Verified', 'Blacklisted'];

// Upload file types
export type AllowedFileType = 'image' | 'excel' | 'apk';

// Additional information for these file types
export const FileUploadConfig: { [key in AllowedFileType]: { accept: Accept; maxSizeMb: number } } = {
  image: {
    accept: {
      'image/*': ['.jpeg', '.png'],
    },
    maxSizeMb: 5,
  },
  excel: {
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/csv': ['.csv'],
      'text/plain': ['.csv'],
    },
    maxSizeMb: 10,
  },
  apk: {
    accept: {
      'application/vnd.android.package-archive': ['.apk'],
    },
    maxSizeMb: 5000,
  },
};

export const getFileTypeConfig = (fileType: AllowedFileType) => FileUploadConfig[fileType];

export const LocationMehod = {
  GPS: 1000,
  NETWORK: 100,
  ANCHOR_TAG: 10,
};

export const ReaderOwnerAccountId = {
  Unilode: '5cf01b88-c11e-4dc7-bf49-bb10b3900e23',
  AirCanada: 'fb78200d-bf65-4d88-b832-5dfe192809ea',
};

export const MarkerReader = {
  markerName: 'Reader Centroid',
};

export const EditorConfig = {
  DISABLE_CLIPBOARD_IMAGE: true,
  DISABLE_UPLOAD_IMAGE: true,
};

export const LocalStorageConfig = {
  SEARCHBAR_FILTERS: 'SEARCHBAR',
  LAST_VISIT_ID: 'LAST_VISIT_ID',
  FLEET_DASHBOARD_STATE: 'FLEET_DASHBOARD_STATE',
  TIME_MODE: 'TIME_MODE',
};

export const SearchBarFilterTypes = {
  READERS: 'readers',
  DEVICES: 'devices',
  DEVICE_INVENTORY: 'deviceInventory',
  USERS: 'users',
  ROLES: 'roles',
  POLICIES: 'policies',
  ADMISSIONS: 'admissions',
  PAIRING_HISTORY: 'pairingHistory',
  EQUIPMENTS: 'equipments',
  FACILITY: 'facility',
  FACILITY_TMP: 'facility_tmp', // filter for one time use
  SCAN_SESSIONS: 'scan_sessions',
  SETTINGS: 'settings',
  SIGHTINGS: 'sightings',
  MOBILE_REPORT: 'mobile_report',
  LABEL: 'labels',
  TASKSQUEUE: 'tasksQueue',
  LANGUAGE: 'languages',
  DEVICE_TYPES: 'deviceTypes',
  COLLECTIONS: 'collections',
  CONTRACTS: 'contracts',
  LUC_TRANSFERS: 'lucTransfers',
  VISIT_HISTORY: 'visitHistory',
  COMPANIES: 'companies',
  EXPORTS: 'exports',
};

export const DefaultFilterSelection = {
  DefaultResourceType: 'all',
};

export const TimeModeType = {
  Local: 'local',
  Utc: 'utc',
};

export const AutoCompleteConfig = {
  OptionType: {
    New: 'new',
  },
  EventType: {
    Clear: 'clear',
    RemoveOption: 'removeOption',
    SelectOption: 'selectOption',
  },
};

export const TasksReaderQueue = {
  AssignToMe: 'Assign to me',
  AssignedToMe: 'Assigned to me',
  WatchThis: 'Watch This',
  StopWatching: 'Stop Watching',
  Status: 'status',
  AssignedUserName: 'assignedUserName',
  Severity: 'severity',
  Category: 'category',
};

export const InventoryContent = {
  manageInventoryTitle: 'Manage Inventory Synchronization and reporting',
  manageInventorysubtitle: 'The last sync time and status. ',
  manageInventorytooltipTitle: 'Auto-sync runs daily at midnight UTC.',
  createdDate: 'Created Date:',
  uploadFile: 'Upload File',
  syncWithErrors: 'Sync completed with errors',
  syncSuccessful: 'Sync successful',
  close: 'Close',
  upload: 'Upload',
  detailSummary: 'Detail Summary',
  createdDevices: 'Created Devices',
  newlyFoundDevices: 'Newly Found Devices',
  totalDevicesFound: 'Total Devices Found',
  createdDateTime: 'Created Date Time',
  errorsFound: 'Errors found on validation',
  Id: 'ID',
  message: 'Message',
  viewImportedData: 'View imported data',
  summaryLbl: 'summary',
  uploadLbl: 'upload',
};

export const Language = {
  heading: 'Add, Edit, Delete Languages',
  createdDate: 'Created Date',
  feildRequiredLabel: 'This field is required',
  updateLanguageSuccess: 'Update Language Label success!',
  updateLanguageFailed: 'Unable to update Language label!',
  removeLanguageSuccess: 'Language has been removed',
  removeLanguageFailed: 'Unable to delete language',
  labelRemoved: 'Label has been removed',
  labelRemovalFailed: 'Unable to delete label',
  updateLanguageDisplayNameSuccess: 'Updated Language Display Name!',
  updateLanguageDisplayNameFailed: 'Failed to update display name for',
  addLanguage: 'Add Language',
  updateLanguage: 'Update Language',
  updateDisplayNameLabel: 'Update Display Name',
  addLanguagelabel: 'Enter the details for the new language. Make sure the key exists in the default language.',
  isoCodeLabel: 'isoCode',
  ISOCodeLabel: 'ISO Code',
  isoCodePlaceholder: 'Enter ISO code',
  enterLabel: 'Enter label',
  enterLabelValue: 'Enter label value',
  valueLabel: 'value',
  displayNameError: 'Display name is required',
  displayNamelabel: 'Display Name',
  bulkUpdate: 'Bulk Update',
  invalidJson: 'Invalid JSON format',
  errorBulkUpload: 'Error in bulk upload',
  errorDuringBulkUpload: 'Cannot save: An error occurred during bulk upload',
  saveChanges: 'Save Changes',
};

export const PairingVerification = {
  PairingVerificationStatus: 'Pairing Verification Status',
};

export const LegendItems = {
  geofence: 'Geofence',
  estimatedReaderRadius: 'Estimated reader radius',
  lockedLocationReader: 'Locked location of reader',
  recentReportLocation: 'Recent report location',
};
export const AssignUserPermissions = {
  noPermissions: 'No permissions available',
  onlyShowAssigned: 'Only show assigned permissions',
  legend_assigned_directly: 'Granted directly to this user',
  legend_assigned_indirectly: 'Granted through a role or inherited through another permission',
  search: 'Search...',
};

export const DeviceTypeSettingsConstants = {
  title: 'Update Device Type Health',
  maxAgeInDaysLabel: 'Max Age in Days',
  maxDaysSinceLastSightingLabel: 'Max Days Since Last Sighting',
  minBatteryVoltageLabel: 'Min Battery Voltage',
  placeholderMaxAgeInDays: 'Enter max age in days',
  placeholderMaxDaysSinceLastSighting: 'Enter max days since last sighting',
  placeholderMinBatteryVoltage: 'Enter min battery voltage',
  saveButtonLabel: 'Save',
  validationRequired: 'This field is required',
  validationPositive: 'Must be a positive number',
  validationInteger: 'Must be an integer',
  maxAgeInDays: 'maxAgeInDays',
  maxDaysSinceLastSighting: 'maxDaysSinceLastSighting',
  minBatteryVoltage: 'minBatteryVoltage',
  name: 'name',
  type: 'type',
  manufacturer: 'manufacturer',
};

export const CollectionMetaAndDetails = {
  createdDate: 'Created Date:',
  collectionName: 'Collection Name',
  station: 'Station',
  itemTypes: 'Item Types',
  noOfItems: 'No of Items',
  collectionMethod: 'Collection Method',
  createdAt: 'Created At',
  updatedOn: 'Updated On',
};

export const ReadersStationMapView = {
  readerCount: 'Reader Count:',
  LoadingMap: 'Loading map...',
  ErrorMapData: 'Error loading map data:',
  NoDataAvailable: 'No data available for locations',
};

export const StockDetailsContent = {
  pageKey: 'STOCK_DETAILS',
  heading: 'Stock & Global Inventory',
  allOptionValue: 'all',
  allOptionLabel: 'All',
  carrierLabel: 'Carrier',
  stationLabel: 'Station',
  equipmentTypeLabel: 'Equipment Type',
  uldTypeLabel: 'ULD Type',
  uldCategoryLabel: 'ULD Category',
  stationsLoadError: 'Could not load stations.',
  filtersLoadError: 'Could not load stock filters.',
};

export const dashboardContent = {
  titles: {
    pairingStatistics: 'Pairing Statistics',
    sightingsStatistics: 'Sightings Statistics',
    globalFleetMap: 'Global Fleet Map',
  },

  messages: {
    noFleetData: 'No fleet data found.',
  },

  comingSoon: {
    dashboard: {
      title: 'Global Fleet Map is coming soon',
    },
    stats: {
      title: 'Stats are on the way',
    },
    superSentinel: {
      title: 'Super Sentinel is coming soon',
    },
  },

  tabs: {
    dashboard: 'Dashboard',
    stats: 'Stats',
    superSentinel: 'Super Sentinel',
  },

  errors: {
    fetchPairing: 'There was an error fetching the pairing data. Please check your network or try again later.',
    fetchSightings: 'There was an error fetching the sightings data. Please check your network or try again later.',
  },

  map: {
    resetToFullView: 'Reset to full view',
    openStreetMapAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
} as const;
